import { supabase } from "./supabaseClient";

const API_BASE = (import.meta.env.VITE_API_BASE || "/api").replace(/\/$/, "");

function buildApiUrl(path: string): string {
  if (path.startsWith("http")) return path;

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  // Backward-compat: if API_BASE already ends in /api, avoid /api/api/* URLs.
  if (API_BASE.endsWith("/api") && normalizedPath.startsWith("/api/")) {
    return `${API_BASE}${normalizedPath.slice(4)}`;
  }

  return `${API_BASE}${normalizedPath}`;
}

const HTTP_METHODS = new Set(["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"]);

/**
 * Safe API fetch that:
 * - Accepts both calling conventions:
 *     apiRequest("/path", init?)              -- current 2-arg style
 *     apiRequest("POST", "/path", bodyData)   -- legacy 3-arg style
 * - Adds Authorization: Bearer <supabase jwt> when available
 * - Does NOT throw on 5xx -- callers decide what to do with status codes
 */
export async function apiRequest(
  methodOrPath: string,
  pathOrInit?: string | RequestInit,
  bodyData?: unknown
): Promise<Response> {
  let path: string;
  let init: RequestInit;

  if (HTTP_METHODS.has(methodOrPath.toUpperCase()) && typeof pathOrInit === "string") {
    // Legacy 3-arg: apiRequest("POST", "/path", bodyData)
    path = pathOrInit;
    const bodyInit = bodyData != null
      ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(bodyData) }
      : {};
    init = { method: methodOrPath.toUpperCase(), ...bodyInit };
  } else {
    // Current 2-arg: apiRequest("/path", init?)
    path = methodOrPath;
    init = (typeof pathOrInit === "object" && pathOrInit !== null
      ? pathOrInit
      : {}) as RequestInit;
  }

  const url = buildApiUrl(path);
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;

  const headers = new Headers(init.headers || {});
  if (token) headers.set("Authorization", `Bearer ${token}`);

  return fetch(url, { credentials: "include", ...init, headers });
}
